/**
 * What civilisations remember of each other, and of everyone's word (ADR-0060).
 *
 * A memory is a kind and a turn; what it is worth, and for how long, is looked up in
 * `rules.diplomacy.memories` whenever attitude is worked out. Reputation is one number
 * per civilisation that everyone else reads: broken treaties and stopped payments lower
 * it, and it recovers slowly.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameState, Id, Memory } from '../state/types.js';
import { ensureRelation, relationOf } from './relation.js';

/** Most memories one side keeps of the other; the oldest go first. */
const MAX_MEMORIES = 60;

/** `who` will remember this about `about`. */
export function remember(state: GameState, who: Id, about: Id, kind: string, weight = 1): void {
  if (who === about || !state.civs[who] || !state.civs[about]) return;
  const relation = ensureRelation(state, who, about);
  const list = relation.a === who ? relation.memoriesA : relation.memoriesB;
  list.push({ kind, turn: state.meta.turn, weight });
  if (list.length > MAX_MEMORIES) list.splice(0, list.length - MAX_MEMORIES);
}

/** What `who` remembers of `about`, oldest first. */
export function memoriesOf(state: GameState, who: Id, about: Id): readonly Memory[] {
  const relation = relationOf(state, who, about);
  if (!relation) return [];
  return relation.a === who ? relation.memoriesA : relation.memoriesB;
}

/** A memory's worth now: its full value at first, fading to nothing. */
export function memoryValue(content: Content, memory: Memory, turn: number): number {
  const rule = content.rules.diplomacy.memories[memory.kind];
  if (!rule) return 0;
  const left = 1 - (turn - memory.turn) / rule.turns;
  return left <= 0 ? 0 : rule.value * memory.weight * left;
}

/** Forget what no longer counts. */
export function pruneMemories(state: GameState, content: Content): void {
  const turn = state.meta.turn;
  const keep = (m: Memory): boolean => {
    const rule = content.rules.diplomacy.memories[m.kind];
    return !!rule && turn - m.turn < rule.turns;
  };
  for (const relation of state.relations) {
    relation.memoriesA = relation.memoriesA.filter(keep);
    relation.memoriesB = relation.memoriesB.filter(keep);
  }
}

/** Someone broke their word: everyone will know. */
export function damageReputation(state: GameState, civId: Id, amount: number): void {
  const civ = state.civs[civId];
  if (civ) civ.reputation = Math.min(0, civ.reputation + amount);
}

/** A turn of good behaviour: reputation recovers towards a clean name. */
export function recoverReputation(state: GameState, content: Content): void {
  const step = content.rules.diplomacy.reputation.recoveryPerTurn;
  for (const civ of Object.values(state.civs)) {
    if (civ.reputation < 0) civ.reputation = Math.min(0, Math.round((civ.reputation + step) * 100) / 100);
  }
}
